import React, { FC } from 'react';

import styled from 'styled-components';

import { Flex, Text } from '@adsk/alloy-react-box';
import Divider from '@adsk/alloy-react-divider';
import { MenuList, MenuCategory } from '@adsk/alloy-react-menu';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';
import theme from '@adsk/alloy-react-theme';
import { Flyout } from '@adsk/alloy-react-tooltip';

import { usePluginsValidator } from './hooks/usePluginsValidator';
import { useTranslation } from './i18n';
import InlineLoader from './InlineLoader';
import { useEntitiesCacheContext } from './providers/EntitiesCacheProvider';
import { useRelationshipsContext } from './providers/RelationshipsProvider';
import { CommonReferencePickerPlugin } from './types/ReferencePickerPlugin';
import type {
  EntitiesCache,
  EntityState,
  RelationshipsData,
} from './types/types';

const StyledText = styled(Text)`
  cursor: pointer;
  &:hover {
    color: ${theme.colors.adskBlue500};
  }
`;

interface LinkedReferencesCellProps {
  sourceEntityId: string;
  sourceEntityDomain: string;
  sourceEntityType: string;
  plugins: CommonReferencePickerPlugin[];
  style?: React.CSSProperties;
  className?: string;
}

interface EntityGroupInTooltipProps {
  entities: EntityState[];
  plugin: CommonReferencePickerPlugin;
  hasDivider: boolean;
  t: ReturnType<typeof useTranslation>['t'];
}

interface GetPluginsEntityMapProps {
  plugins: CommonReferencePickerPlugin[];
  relationshipsData: RelationshipsData[];
  entitiesCache: EntitiesCache;
}

const getPluginsEntityMap = ({
  plugins,
  relationshipsData,
  entitiesCache,
}: GetPluginsEntityMapProps) => {
  return plugins
    .map((plugin) => {
      const relationships = relationshipsData.filter(
        ({ domain, type }) =>
          domain === plugin.metadata.domain && type === plugin.metadata.type,
      );

      const allEntities = relationships.map(
        ({ domain, type, entityId }) =>
          entitiesCache?.[domain]?.[type]?.[entityId],
      );

      const loading = allEntities.some((e) => e?.loading);
      const entities = allEntities.filter((e) => e?.entity);
      return { plugin, entities, loading };
    })
    .filter(({ entities, loading }) => loading || entities.length);
};

const EntityGroupInTooltip: FC<EntityGroupInTooltipProps> = ({
  entities,
  plugin,
  hasDivider,
  t,
}) => {
  const { EntityMenuItem } = plugin;
  return entities.length ? (
    <MenuCategory title={<plugin.PluralText />}>
      <MenuList style={{ paddingBottom: 12 }}>
        {EntityMenuItem ? (
          entities.map(
            ({ entity }) =>
              entity && (
                <EntityMenuItem
                  entity={entity}
                  renderIcon={plugin.renderIcon}
                  key={entity.id}
                />
              ),
          )
        ) : (
          <div style={{ padding: '0 15px' }}>
            {t('REFERENCES', { count: entities.length })}
          </div>
        )}
      </MenuList>
      {hasDivider && <Divider />}
    </MenuCategory>
  ) : null;
};

const LinkedReferencesCell: FC<LinkedReferencesCellProps> = ({
  sourceEntityId,
  sourceEntityDomain,
  sourceEntityType,
  plugins,
  style,
  className,
}) => {
  const { t } = useTranslation();
  const { relationshipDataBySource } = useRelationshipsContext();
  const { relationshipsData = [], loading: relationshipLoading } =
    relationshipDataBySource?.[sourceEntityDomain]?.[sourceEntityType]?.[
      sourceEntityId
    ] || {};

  usePluginsValidator({
    targetPlugins: plugins,
    source: 'LinkedReferencesCell',
  });

  const { entitiesCache } = useEntitiesCacheContext();

  const viewablePlugins = plugins.filter(({ isPickerOnly }) => !isPickerOnly);
  const pluginsEntityMap = getPluginsEntityMap({
    plugins: viewablePlugins,
    relationshipsData,
    entitiesCache,
  });

  const size = Object.values(pluginsEntityMap).reduce(
    (acc, data) => acc + data.entities.length,
    0,
  );
  const entitiesLoading = pluginsEntityMap.some(({ loading }) => loading);
  if (relationshipLoading || entitiesLoading) {
    return <InlineLoader />;
  }
  const firstItem = pluginsEntityMap.length
    ? pluginsEntityMap[0].entities[0]
    : null;
  if (!firstItem) {
    return '-';
  }

  const tooltipContent = () => {
    const mappedEntityTypes = Object.values(pluginsEntityMap).filter(
      ({ entities }) => entities.length,
    );
    return (
      <>
        {mappedEntityTypes.map(({ entities, plugin }, index) => (
          <EntityGroupInTooltip
            entities={entities}
            plugin={plugin}
            key={plugin.metadata.type}
            hasDivider={index < mappedEntityTypes.length - 1}
            t={t}
          />
        ))}
      </>
    );
  };

  const tooltip = tooltipContent();
  const cellValue = (
    <Flex
      maxWidth="100%"
      style={{ ...style, whiteSpace: 'nowrap' }}
      className={className}
      data-testid="linkedReferenceCellValue"
    >
      <StyledText color="adskBlue700">
        {t('REFERENCES', { count: size })}
      </StyledText>
    </Flex>
  );

  return (
    <Flyout
      content={tooltip}
      trigger={Flyout.TRIGGER_EVENTS.HOVER}
      triggerTarget={Flyout.TRIGGER_TARGETS.TOOLTIP}
      style={{
        padding: '1px 0',
        maxHeight: 272,
        overflowY: 'auto',
      }}
    >
      {cellValue}
    </Flyout>
  );
};

export default WithAlloySuspense(LinkedReferencesCell);
